import React, { useCallback, useMemo, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
} from "reactflow";
import "reactflow/dist/style.css";
import CourseGraphNode from "./CourseGraphNode";
import type { CourseNodeWithStatus } from "@/types/courses";

const nodeTypes = { courseNode: CourseGraphNode };

interface CourseGraphCanvasProps {
  nodes: CourseNodeWithStatus[];
}

/** Simple top-down layered layout using order_index grouping */
function buildLayout(courseNodes: CourseNodeWithStatus[]) {
  // Group nodes by their depth (based on prerequisite chains)
  const depthMap = new Map<string, number>();

  function getDepth(nodeId: string, visited: Set<string>): number {
    if (depthMap.has(nodeId)) return depthMap.get(nodeId)!;
    if (visited.has(nodeId)) return 0;
    visited.add(nodeId);

    const node = courseNodes.find((n) => n.id === nodeId);
    if (!node || !node.prerequisites || node.prerequisites.length === 0) {
      depthMap.set(nodeId, 0);
      return 0;
    }

    const maxParentDepth = Math.max(
      ...node.prerequisites.map((pid) => getDepth(pid, visited))
    );
    const depth = maxParentDepth + 1;
    depthMap.set(nodeId, depth);
    return depth;
  }

  courseNodes.forEach((n) => getDepth(n.id, new Set()));

  // Group by depth
  const layers = new Map<number, CourseNodeWithStatus[]>();
  courseNodes.forEach((n) => {
    const d = depthMap.get(n.id) ?? 0;
    if (!layers.has(d)) layers.set(d, []);
    layers.get(d)!.push(n);
  });

  const NODE_W = 200;
  const NODE_H = 70;
  const GAP_X = 60;
  const GAP_Y = 120;

  const flowNodes: Node[] = [];
  const sortedDepths = [...layers.keys()].sort((a, b) => a - b);

  // Calculate max layer width for centering
  const maxLayerWidth = Math.max(
    ...sortedDepths.map((d) => {
      const count = layers.get(d)!.length;
      return count * NODE_W + (count - 1) * GAP_X;
    })
  );

  sortedDepths.forEach((depth) => {
    const layerNodes = layers.get(depth)!;
    const layerWidth = layerNodes.length * NODE_W + (layerNodes.length - 1) * GAP_X;
    const offsetX = (maxLayerWidth - layerWidth) / 2;

    layerNodes.forEach((cn, i) => {
      flowNodes.push({
        id: cn.id,
        type: "courseNode",
        position: {
          x: offsetX + i * (NODE_W + GAP_X),
          y: depth * (NODE_H + GAP_Y),
        },
        data: {
          title: cn.title,
          status: cn.status,
          highlighted: false,
          dimmed: false,
        },
      });
    });
  });

  // Build edges from prerequisites
  const flowEdges: Edge[] = [];
  courseNodes.forEach((cn) => {
    if (cn.prerequisites) {
      cn.prerequisites.forEach((pid) => {
        flowEdges.push({
          id: `${pid}->${cn.id}`,
          source: pid,
          target: cn.id,
          style: { stroke: "hsl(var(--border))", strokeWidth: 1.5 },
          animated: false,
        });
      });
    }
  });

  return { flowNodes, flowEdges };
}

export const CourseGraphCanvas: React.FC<CourseGraphCanvasProps> = ({
  nodes: courseNodes,
}) => {
  const { flowNodes: initialNodes, flowEdges: initialEdges } = useMemo(
    () => buildLayout(courseNodes),
    [courseNodes]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Build adjacency for highlighting
  const adjacency = useMemo(() => {
    const map = new Map<string, Set<string>>();
    courseNodes.forEach((cn) => {
      if (!map.has(cn.id)) map.set(cn.id, new Set());
      if (cn.prerequisites) {
        cn.prerequisites.forEach((pid) => {
          map.get(cn.id)!.add(pid);
          if (!map.has(pid)) map.set(pid, new Set());
          map.get(pid)!.add(cn.id);
        });
      }
    });
    return map;
  }, [courseNodes]);

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const clickedId = node.id;
      const newSelected = selectedId === clickedId ? null : clickedId;
      setSelectedId(newSelected);

      const related = newSelected ? adjacency.get(newSelected) ?? new Set() : new Set();

      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: {
            ...n.data,
            highlighted: newSelected ? n.id === newSelected || related.has(n.id) : false,
            dimmed: newSelected ? n.id !== newSelected && !related.has(n.id) : false,
          },
        }))
      );

      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          style: {
            stroke:
              newSelected && (e.source === newSelected || e.target === newSelected)
                ? "hsl(var(--primary))"
                : "hsl(var(--border))",
            strokeWidth:
              newSelected && (e.source === newSelected || e.target === newSelected)
                ? 2.5
                : 1.5,
          },
          animated:
            newSelected !== null &&
            (e.source === newSelected || e.target === newSelected),
        }))
      );
    },
    [selectedId, adjacency, setNodes, setEdges]
  );

  const onPaneClick = useCallback(() => {
    setSelectedId(null);
    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        data: { ...n.data, highlighted: false, dimmed: false },
      }))
    );
    setEdges((eds) =>
      eds.map((e) => ({
        ...e,
        style: { stroke: "hsl(var(--border))", strokeWidth: 1.5 },
        animated: false,
      }))
    );
  }, [setNodes, setEdges]);

  return (
    <div className="w-full h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
        minZoom={0.3}
        maxZoom={1.5}
      >
        <Background gap={20} size={1} color="hsl(var(--border) / 0.3)" />
        <Controls
          showInteractive={false}
          className="!bg-background !border-border !shadow-md [&>button]:!bg-background [&>button]:!border-border [&>button]:!text-foreground [&>button:hover]:!bg-muted"
        />
        <MiniMap
          className="hidden md:block !bg-background !border-border"
          nodeColor={(n) => {
            switch (n.data?.status) {
              case "completed":
                return "hsl(var(--correct))";
              case "in_progress":
                return "hsl(var(--primary))";
              default:
                return "hsl(var(--border))";
            }
          }}
          maskColor="hsl(var(--background) / 0.7)"
        />
      </ReactFlow>
    </div>
  );
};
